import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { BulkWorkItemStatusFailureReason } from '@/types'
import { CircleCheck, RotateCcw, TriangleAlert, X } from 'lucide-react'
import { formatWorkItemNumber } from '@/lib/workItemFormat'

export interface BulkStatusResultItem {
  id: string
  number: number | null
  title: string
  currentStatus: string | null
  reason?: BulkWorkItemStatusFailureReason
  errorMessage?: string
}

interface BulkStatusResultDialogProps {
  targetStatus: string
  updatedItems: BulkStatusResultItem[]
  failedItems: BulkStatusResultItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onRetry: () => void
  isRetrying: boolean
}

function ResultRow({ item, testId }: { item: BulkStatusResultItem; testId: string }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
      data-testid={testId}
    >
      <span className="w-12 shrink-0 text-sm font-medium text-muted-foreground">
        {formatWorkItemNumber(item.number)}
      </span>
      <span className="flex-1 min-w-0 text-sm font-bold text-foreground truncate">"{item.title}"</span>
      <span className="shrink-0">
        {item.currentStatus !== null ? (
          <WorkItemStatusBadge status={item.currentStatus} />
        ) : (
          <span className="text-sm text-muted-foreground">
            {item.reason === 'NotFound' ? 'Deleted' : 'Changed by someone else'}
          </span>
        )}
      </span>
    </div>
  )
}

export function BulkStatusResultDialog({
  targetStatus,
  updatedItems,
  failedItems,
  open,
  onOpenChange,
  onRetry,
  isRetrying,
}: BulkStatusResultDialogProps) {
  const retryableCount = failedItems.filter((item) => item.currentStatus !== null).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated"
        hideCloseButton
        data-testid="bulk-status-result-dialog"
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Some work items were not updated
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close dialog">
            <X className="h-6 w-6" />
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          {updatedItems.length} of {updatedItems.length + failedItems.length} work items moved to{' '}
          <span className="font-bold">{targetStatus}</span>. The rest still read the status shown below.
        </DialogDescription>

        <div className="flex flex-col gap-4 max-h-[320px] overflow-y-auto">
          <section>
            <p className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
              <TriangleAlert className="h-4 w-4 text-danger-foreground" />
              Not updated ({failedItems.length})
            </p>
            <div className="flex flex-col gap-1">
              {failedItems.map((item) => (
                <ResultRow key={item.id} item={item} testId="bulk-status-failed-row" />
              ))}
            </div>
          </section>

          {updatedItems.length > 0 && (
            <section>
              <p className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                <CircleCheck className="h-4 w-4 text-success-foreground" />
                Updated ({updatedItems.length})
              </p>
              <div className="flex flex-col gap-1">
                {updatedItems.map((item) => (
                  <ResultRow key={item.id} item={item} testId="bulk-status-updated-row" />
                ))}
              </div>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isRetrying}
            data-testid="bulk-status-done-button"
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Done
          </Button>
          {retryableCount > 0 && (
            <Button
              onClick={onRetry}
              disabled={isRetrying}
              data-testid="bulk-status-retry-button"
              className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
            >
              <RotateCcw className="h-6 w-6" />
              {isRetrying ? 'Retrying...' : `Retry ${retryableCount} failed`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
